/**
 * Nova Derma — system-wide constants.
 * Frozen file — do not edit during feature work.
 */

/** NFR-10 — every date and time in the clinic is Jordan time. */
export const CLINIC_TIMEZONE = 'Asia/Amman';

/** Jordanian Dinar carries three decimal places (fils), not two. */
export const CURRENCY_CODE = 'JOD';
export const CURRENCY_SYMBOL_AR = 'د.أ';
export const CURRENCY_SYMBOL_EN = 'JD';
export const MONEY_DECIMALS = 3;

/** Quantities are stored with four decimals so a 1/3-of-a-vial split stays exact. */
export const QUANTITY_DECIMALS = 4;

/** NFR-03 — Arabic is the default; each user can switch their own. */
export const DEFAULT_LANGUAGE = 'AR';

/** CAL-01 — default slot size; adjustable clinic-wide (CAL-02), never per provider. */
export const DEFAULT_SLOT_MINUTES = 15;
export const ALLOWED_SLOT_MINUTES = [5, 10, 15, 20, 30, 60] as const;

/** APT-08 — how long after the appointment before it lands in "needs closure". */
export const DEFAULT_NEEDS_CLOSURE_AFTER_HOURS = 5;

/** APT-13 / DOC-03 — wait counter turns red past this. PRD §9 Q7 is still open. */
export const DEFAULT_WAIT_TIME_ALERT_MINUTES = 20;

/** INV-67 — expiry warning window, overridable per item. */
export const DEFAULT_EXPIRY_ALERT_DAYS = 60;

/** INV-101 — an item with no movement for this long is dormant money. */
export const DEFAULT_DORMANT_ITEM_MONTHS = 6;

/** MSG-06 — GSM-7 vs UCS-2 segment sizes, used by the character counter. */
export const SMS_GSM7_SINGLE = 160;
export const SMS_GSM7_MULTI = 153;
export const SMS_UCS2_SINGLE = 70;
export const SMS_UCS2_MULTI = 67;

/**
 * PAT-01 — which name parts are mandatory by default, in the clinic's writing
 * order (الأول، الأب، الجد، العائلة): the first three, so the family name stays
 * optional. Stored per part so a clinic can make any single part optional while
 * keeping a later one mandatory (e.g. الاسم الثالث اختياري والعائلة إجباري).
 */
export const DEFAULT_REQUIRED_NAME_PARTS: boolean[] = [true, true, true, false];

/** PAT-02 — default dialling code; numbers are stored in E.164. */
export const DEFAULT_COUNTRY_CODE = 'JO';
export const DEFAULT_DIAL_CODE = '+962';

/** MSG-05 — the placeholders a template may contain. */
export const TEMPLATE_VARIABLES = [
  'patientName',
  'date',
  'time',
  'provider',
  'procedure',
  'clinicName',
  'clinicPhone',
  'balance',
  'confirmUrl',
  'cancelUrl',
  'invoiceUrl',
] as const;
export type TemplateVariable = (typeof TEMPLATE_VARIABLES)[number];

/** Arabic labels for the template variables, as the doctor will see them. */
export const TEMPLATE_VARIABLE_LABELS_AR: Record<TemplateVariable, string> = {
  patientName: 'اسم المريض',
  date: 'التاريخ',
  time: 'الوقت',
  provider: 'الطبيب',
  procedure: 'الإجراء',
  clinicName: 'اسم العيادة',
  clinicPhone: 'هاتف العيادة',
  balance: 'الرصيد المستحق',
  confirmUrl: 'رابط التأكيد',
  cancelUrl: 'رابط الإلغاء',
  invoiceUrl: 'رابط الفاتورة',
};

/** DS-53 / PROV-01 — the palette offered when picking a provider colour. */
export const PROVIDER_COLORS = [
  '#2563eb', // blue-600
  '#059669', // emerald-600
  '#d97706', // amber-600
  '#dc2626', // red-600
  '#7c3aed', // violet-600
  '#0891b2', // cyan-600
  '#db2777', // pink-600
  '#65a30d', // lime-600
  '#ea580c', // orange-600
  '#4f46e5', // indigo-600
] as const;

/** Pagination defaults shared by every list endpoint. */
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

/** DS-55 — keyboard shortcuts are shown in the UI, not hidden. */
export const KEYBOARD_SHORTCUTS = {
  NEW_APPOINTMENT: 'F2',
  PATIENT_SEARCH: 'F3',
  NEW_PATIENT: 'F4',
  NEW_INVOICE: 'F6',
  TODAY: 'F8',
  SAVE: 'Ctrl+S',
  CLOSE_DIALOG: 'Escape',
} as const;
